import React, { useEffect, useRef } from 'react';

const bdOpportunities = [
  { 
    title: 'Garments & Textiles', 
    desc: 'World-leading manufacturing ecosystem with strong expertise in knitwear, denim, and large-scale production.' 
  },
  { 
    title: 'Pharmaceutical Industry', 
    desc: "One of South Asia's fastest-growing pharmaceutical industries with increasing export potential." 
  },
  { 
    title: 'ICT & Digital Economy', 
    desc: 'Rapidly expanding digital ecosystem supported by a young workforce and growing consumer market.' 
  },
  { 
    title: 'Infrastructure & Energy', 
    desc: 'Major infrastructure and energy investments supporting long-term economic growth.' 
  },
  { 
    title: 'Agro-processing and fertilizers', 
    desc: 'Growing opportunities in food processing, agriculture, fertilizers, and value-added exports.' 
  },
   { 
    title: 'Ceramics & Building Materials', 
    desc: 'Growing opportunities for collaboration in ceramics, tiles, sanitaryware, construction materials, architectural solutions and manufacturing technologies between Bangladesh and Italy.' 
  },
  { 
    title: 'Chemicals & Industrial Solutions', 
    desc: 'Opportunities for cooperation in specialty chemicals, industrial inputs, advanced materials, textile chemicals, construction chemicals, water treatment solutions and manufacturing support technologies.' 
  },
];

const itOpportunities = [
  { 
    title: 'Luxury Fashion & Leather', 
    desc: "Access to Italy's globally recognized fashion, design, and leather industries." 
  },
  { 
    title: 'Machinery & Equipment', 
    desc: 'Italian engineering excellence supporting industrial modernization and manufacturing efficiency.' 
  },
  { 
    title: 'Food & Agri-food', 
    desc: 'Premium Italian food products and technologies for a growing Bangladeshi consumer market.' 
  },
  { 
    title: 'Tourism & Hospitality', 
    desc: 'Opportunities for bilateral tourism promotion, hospitality investment, and cultural exchange.' 
  },
  { 
    title: 'Renewable Energy Tech', 
    desc: "Italian expertise supporting Bangladesh's energy transition and industrial modernization goals." 
  },
  { 
    title: 'Furniture, Interior Design & Living Solutions', 
    desc: 'Access to Italy\'s world-renowned furniture, interior design, lighting and premium living industries, creating opportunities for trade, investment, project development and business partnerships between Bangladesh and Italy.' 
  },
 
];

// --- Shared Reveal Logic ---
function useReveal() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.querySelectorAll<HTMLElement>('[data-r]').forEach((node, i) => {
            setTimeout(() => {
              node.style.opacity = '1';
              node.style.transform = 'translateY(0) translateX(0)';
            }, i * 120);
          });
          obs.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return ref;
}

const hide = (dir: 'up' | 'left' | 'right' = 'up'): React.CSSProperties => ({
  opacity: 0,
  transform:
    dir === 'up' ? 'translateY(28px)'
    : dir === 'left' ? 'translateX(-32px)'
    : 'translateX(32px)',
  transition: 'opacity 0.8s ease, transform 0.8s ease',
});
// ---------------------------

export default function TradeSection() {
  const sectionRef = useReveal();

  return (
    <section id="trade" className="bg-white py-20 md:py-24 font-sans">
      <div ref={sectionRef} className="w-full px-6 md:px-10 lg:px-16 xl:px-24 max-w-[1400px] mx-auto">
        
        {/* Section Headers */}
        <div className="max-w-3xl mx-auto text-center mb-14 flex flex-col items-center">
          <h2 
            data-r 
            style={{
              ...hide('up'),
              color: '#0F1C35',
              fontSize: 'clamp(2rem, 4vw, 3rem)',
              fontWeight: 800,
              letterSpacing: '-0.02em',
              marginBottom: '16px'
            }}
          >
            Trade & Investment Hub
          </h2> 
          <p 
            data-r 
            style={{ ...hide('up'), color: '#3D4A62' }}
            className="text-base md:text-lg leading-relaxed"
          >
            Discover strategic opportunities on both sides of our bilateral relationship
          </p>
        </div>

        {/* Trade Cards Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          
          {/* Bangladesh Card */}
          <div 
            data-r 
            className="relative rounded-2xl p-8 md:p-12 overflow-hidden flex flex-col group shadow-sm hover:shadow-lg transition-shadow duration-500"
            style={{ 
              ...hide('up'),
              backgroundColor: '#F8F7F3',
              border: '1px solid #E5E7EB'
            }}
          >
            {/* Decorative Dot Grid */}
            <div 
              className="absolute -right-6 -top-6 opacity-[0.08] transition-opacity duration-700 group-hover:opacity-[0.15]"
              style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '8px' }}
            >
              {Array.from({ length: 36 }).map((_, i) => (
                <div key={i} style={{ width: '4px', height: '4px', borderRadius: '50%', background: '#0F1C35' }} />
              ))}
            </div>

            <div className="relative z-10 flex items-center gap-5 mb-10 pb-8 border-b border-gray-200">
              <div>
                <h3 className="text-2xl font-bold" style={{ color: '#0F1C35' }}>Strategic Opportunities in Bangladesh</h3>
              </div>
            </div>

            <ul className="space-y-6 relative z-10">
              {bdOpportunities.map((item) => (
                <li key={item.title} className="flex items-start gap-4">
                  <div className="mt-1 shrink-0" style={{ color: '#0077b6' }}>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M5 12h14M12 5l7 7-7 7" />
                    </svg>
                  </div>
                  <div>
                    <div className="text-base font-bold mb-1" style={{ color: '#0F1C35' }}>{item.title}</div>
                    <div className="text-sm leading-relaxed" style={{ color: '#3D4A62' }}>{item.desc}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* Italy Card */}
          <div 
            data-r 
            className="relative rounded-2xl p-8 md:p-12 overflow-hidden flex flex-col group shadow-sm hover:shadow-lg transition-shadow duration-500"
            style={{ 
              ...hide('up'),
              backgroundColor: '#F8F7F3',
              border: '1px solid #E5E7EB'
            }}
          >
            {/* Decorative Dot Grid */}
            <div 
              className="absolute -right-6 -top-6 opacity-[0.08] transition-opacity duration-700 group-hover:opacity-[0.15]"
              style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '8px' }}
            >
              {Array.from({ length: 36 }).map((_, i) => (
                <div key={i} style={{ width: '4px', height: '4px', borderRadius: '50%', background: '#0F1C35' }} />
              ))}
            </div>

            <div className="relative z-10 flex items-center gap-5 mb-10 pb-8 border-b border-gray-200">
              <div>
                <h3 className="text-2xl font-bold" style={{ color: '#0F1C35' }}>Strategic Opportunities in Italy</h3>
              </div>
            </div>

            <ul className="space-y-6 relative z-10">
              {itOpportunities.map((item) => (
                <li key={item.title} className="flex items-start gap-4">
                  <div className="mt-1 shrink-0" style={{ color: '#0077b6' }}>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M5 12h14M12 5l7 7-7 7" />
                    </svg>
                  </div>
                  <div>
                    <div className="text-base font-bold mb-1" style={{ color: '#0F1C35' }}>{item.title}</div>
                    <div className="text-sm leading-relaxed" style={{ color: '#3D4A62' }}>{item.desc}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>

        </div>
      </div>
    </section>
  );
}